import BaseModel from 'volta/models/base-model';
import { collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { formatISODate } from "volta/utils/date-utils";

import type { IResourceName } from 'volta/models/base-model';
import type { TResetDayPayload, TUpdateDayPayload } from 'volta/models/types/calendar';

export const WarehouseCalendarDayResourceName: IResourceName = {
  singular: 'warehouseCalendarDay',
  plural: 'warehouseCalendarDays'
};

export enum WarehouseCalendarDayEvents {
  CalendarUpdated = 'CalendarUpdated',
  DaysReset = 'DaysReset',
  WarehouseCalendarDayReset = 'WarehouseCalendarDayReset'
}

export enum WarehouseCalendarDayCommands {
  UpdateCalendar = 'UpdateCalendar',
  ResetDays = 'ResetDays',
  ResetOne = 'ResetOne'
}

export default class WarehouseCalendarDay extends BaseModel {
  /**
   * Create or update the warehouse calendar days
   * @return The list of created or updated WarehouseCalendarDay models
   */
  static updateCalendar = collectionCommand<
    TUpdateDayPayload,
    WarehouseCalendarDay[],
    typeof WarehouseCalendarDay
  >(WarehouseCalendarDayCommands.UpdateCalendar);

  /**
   * Effectively deletes the models identified by the warehouse id and dates
   * @return Done
   */
  static resetDays = collectionCommand<TResetDayPayload, DoneResult, typeof WarehouseCalendarDay>(
    WarehouseCalendarDayCommands.ResetDays
  );

  /**
   * Effectively deletes the model identified by the entityId
   * @return Done
   */
  resetOne = command<void, WarehouseCalendarDay>(WarehouseCalendarDayCommands.ResetOne);

  @attr('string') warehouseId!: string;
  @attr('date') date!: Date;
  @attr('boolean') openForDemand!: boolean;
  @attr('boolean') openForReception!: boolean;
  @attr('boolean') openForProduction!: boolean;

  get formattedDate() {
    return formatISODate(this.date);
  }

  get indexedDay() {
    const { openForProduction, openForDemand, openForReception } = this;
    return {
      [formatISODate(this.date)]: {
        openForProduction,
        openForDemand,
        openForReception
      }
    };
  }

  [Type] = 'warehouse-calendar-day' as const;
}
